/**
 * Who this campaign goes to.
 *
 * Two sources, and they count different units: the register mirror counts
 * COMPANIES, the scanner leads count DOMAINS. Switching between them is not a
 * filter change, it is a change of what "3 000" means, so the source sits at
 * the top and the preview restates the unit every time.
 *
 * The filter form pushes a rebuilt audience object up on every edit. Whether
 * that counts as a change is decided by `sameAudience`, not by object identity
 * - see lib.ts for why a plain comparison would leave the form permanently
 * dirty after the first save.
 *
 * This file is the wiring: source, chips, form, exclusions, preview. Each of
 * those is its own component, because the version where they were one file had
 * a filter row, an exclusion row and a preview column that all looked alike and
 * meant different things.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Users } from "lucide-react";
import { useT } from "../../../../i18n/I18nProvider";
import {
  ANY,
  EMPTY_AUDIENCE_FILTER,
  applyPatch,
  hasAnyFilter,
  registerAudience,
  paramsToAudience,
  type AudienceFilter,
} from "../../../../lib/audienceFilter";
import { audienceChips, type AudienceChip } from "../../../../lib/audienceChips";
import { activityLabel } from "../../../../lib/emtak";
import {
  getRegisterCoverage,
  previewAudience,
  type Audience,
  type AudiencePreview,
  type Campaign,
  type RegisterCoverage,
  type ScanAudienceFilter,
} from "../../../../lib/marketing";
import { canon, pruneFilter, sameAudience } from "../lib";
import { Panel } from "../shared";
import { RegisterFilterForm } from "./RegisterFilterForm";
import { ScanFilterForm } from "./ScanFilterForm";
import { PreviewCard } from "./preview/PreviewCard";
import { SharedAddresses } from "./SharedAddresses";
import { SourceSwitch } from "./audience/SourceSwitch";
import { FilterChips } from "./audience/FilterChips";
import { ExclusionsCard, type Exclusions } from "./audience/ExclusionsCard";

/** Filters that read a column a sync dataset has to fill in. When one of those
 *  columns is empty, the filter cannot match a single row - and an empty result
 *  looks exactly like a narrow one, so the preview has to be told which of them
 *  are switched on. Keyed by the coverage field that backs each filter. */
const WEALTH_FILTERS = [
  { id: "revenue", column: "withRevenue", active: (f: AudienceFilter) => !!f.revenueMin || !!f.revenueMax },
  { id: "employees", column: "withEmployees", active: (f: AudienceFilter) => !!f.employeesMin || !!f.employeesMax },
  { id: "profitable", column: "withReport", active: (f: AudienceFilter) => f.profitable !== ANY },
] as const;

export function AudiencePanel({
  campaign,
  onChange,
  disabled,
}: {
  campaign: Campaign;
  onChange: (p: Partial<Campaign>) => void;
  disabled?: boolean;
}) {
  const t = useT();
  const saved = campaign.audience;

  /**
   * Has the operator actually touched this form yet.
   *
   * The panel derives an audience object from its own state, and that object is
   * never null: with nothing saved it comes out as "the whole register", which
   * is a real selection and a perfectly valid one to choose. But nobody chose
   * it. Without this gate the derived default was pushed up on mount, so a
   * campaign with no audience arrived already dirty, the readiness strip's
   * blocking "no audience" check ticked itself off, and Activate then failed on
   * the server for exactly the reason the strip had just stopped showing.
   *
   * A ref rather than state: every touch also replaces one of the pieces
   * `audience` is memoised on, so the effect below re-runs on its own and does
   * not need a render of its own to notice.
   */
  const touched = useRef(false);
  const markTouched = useCallback(() => {
    touched.current = true;
  }, []);

  const [source, setSourceState] = useState<"register" | "scanLeads">(saved?.source ?? "register");

  const [regFilter, setRegFilter] = useState<AudienceFilter>(() =>
    saved?.source === "register" ? paramsToAudience(saved.filter) : EMPTY_AUDIENCE_FILTER,
  );
  const [scanFilter, setScanFilter] = useState<ScanAudienceFilter>(() =>
    saved?.source === "scanLeads" ? saved.filter : {},
  );
  const [excl, setExclState] = useState<Exclusions>(() => ({
    excludeFreemail: saved?.filter.excludeFreemail ?? false,
    excludeRoleAccounts: saved?.filter.excludeRoleAccounts ?? false,
  }));

  const setSource = (v: "register" | "scanLeads") => {
    markTouched();
    setSourceState(v);
  };
  const setExcl = (v: Exclusions) => {
    markTouched();
    setExclState(v);
  };

  // Bumped whenever the filter is replaced from outside the form (a reset, a
  // removed chip, or a different campaign), so the text inputs reseed their
  // typing buffers exactly then and not on every committed keystroke.
  const [resetKey, setResetKey] = useState("0");
  const reseed = useCallback(() => setResetKey((k) => String(Number(k) + 1)), []);

  const patchReg = useCallback(
    (p: Partial<AudienceFilter>) => {
      markTouched();
      setRegFilter((f) => applyPatch(f, p));
    },
    [markTouched],
  );
  const patchScan = useCallback(
    (p: Partial<ScanAudienceFilter>) => {
      markTouched();
      setScanFilter((f) => ({ ...f, ...p }));
    },
    [markTouched],
  );

  // How much of the mirror each selectable column actually holds. Read once:
  // it only changes on a sync, and the server memoises it anyway.
  const [coverage, setCoverage] = useState<RegisterCoverage | null>(null);
  useEffect(() => {
    let alive = true;
    getRegisterCoverage()
      .then((c) => alive && setCoverage(c))
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, []);

  /** The audience as the server will store it. */
  const audience = useMemo<Audience>(() => {
    if (source === "register") {
      return { source: "register", filter: registerAudience(regFilter, excl) };
    }
    return { source: "scanLeads", filter: pruneFilter({ ...scanFilter, ...excl }) };
  }, [source, regFilter, scanFilter, excl]);

  // Push upwards only on a real change, and only once the operator has made
  // one. Without `sameAudience` the round trip through jsonb would reorder the
  // keys and the campaign would look dirty the moment it loaded; without
  // `touched` a campaign that has no audience at all would be handed the
  // default one for free - see the ref's own note.
  useEffect(() => {
    if (!touched.current) return;
    if (!sameAudience(audience, saved)) onChange({ audience });
  }, [audience, saved, onChange]);

  // ── Preview ─────────────────────────────────────────────────────────────
  const [preview, setPreview] = useState<AudiencePreview | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const lastRun = useRef<string>("");

  const key = useMemo(
    () =>
      canon({
        audience,
        recontactDays: campaign.recontactDays,
        skipShared: campaign.skipShared,
        sharedAddressThreshold: campaign.sharedAddressThreshold,
        dailyCap: campaign.dailyCap,
      }),
    [audience, campaign.recontactDays, campaign.skipShared, campaign.sharedAddressThreshold, campaign.dailyCap],
  );

  // True between an edit and the debounce firing: the numbers on screen still
  // describe the previous filter. Shown rather than left to be misread as fresh.
  const stale = preview != null && key !== lastRun.current;

  const run = useCallback(
    async (force: boolean) => {
      if (!force && key === lastRun.current) return;
      lastRun.current = key;
      setLoading(true);
      try {
        const res = await previewAudience({
          audience,
          recontactDays: campaign.recontactDays,
          skipShared: campaign.skipShared,
          sharedAddressThreshold: campaign.sharedAddressThreshold,
          // The preview must describe a real day of sending, not an unbounded
          // count nobody will ever reach.
          maxSendable: campaign.dailyCap,
        });
        setPreview(res);
        setError(null);
      } catch {
        setError(t("marketing.error.preview"));
      } finally {
        setLoading(false);
      }
    },
    [key, audience, campaign.recontactDays, campaign.skipShared, campaign.sharedAddressThreshold, campaign.dailyCap, t],
  );

  // Debounced: a preview over the whole mirror is not something to fire on
  // every keystroke, even a committed one.
  useEffect(() => {
    const id = setTimeout(() => void run(false), 600);
    return () => clearTimeout(id);
  }, [run]);

  function clearAll() {
    markTouched();
    setRegFilter(EMPTY_AUDIENCE_FILTER);
    setScanFilter({});
    reseed();
  }

  /** Removing one chip clears its keys and reseeds the typing buffers in the
   *  same step - otherwise the debounce commits the just-cleared value back. */
  function removeChip(chip: AudienceChip) {
    patchReg(chip.clear);
    reseed();
  }

  // The activity chip is the one label this module can improve on: the shared
  // builder only has the raw EMTAK code, and the readable name lives in a table
  // the caller owns.
  const chips = useMemo(() => {
    return audienceChips(regFilter, t).map((c) =>
      c.key === "emtak" ? { ...c, label: activityLabel(regFilter.emtak) } : c,
    );
  }, [regFilter, t]);

  /** Active filters whose backing column holds nothing. The preview explains a
   *  zero with these rather than leaving the operator to find the bug. */
  const deadFilters = useMemo(() => {
    if (!coverage || source !== "register") return [];
    return WEALTH_FILTERS.filter((w) => w.active(regFilter) && coverage[w.column] === 0).map((w) =>
      t(`marketing.audience.${w.id}`),
    );
  }, [coverage, source, regFilter, t]);

  return (
    <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_24rem]">
      <div className="space-y-4">
        <Panel
          title={t("marketing.audience.title")}
          description={t("marketing.audience.intro")}
          icon={<Users className="size-4" />}
        >
          <div className="space-y-4">
            <SourceSwitch value={source} onChange={setSource} disabled={disabled} />

            {source === "register" && (
              <FilterChips
                chips={chips}
                onRemove={removeChip}
                onClearAll={clearAll}
                disabled={disabled || !hasAnyFilter(regFilter)}
              />
            )}

            {source === "register" ? (
              <RegisterFilterForm
                filter={regFilter}
                onChange={patchReg}
                disabled={disabled}
                resetKey={resetKey}
                coverage={coverage}
              />
            ) : (
              <ScanFilterForm filter={scanFilter} onChange={patchScan} disabled={disabled} />
            )}
          </div>
        </Panel>

        <ExclusionsCard value={excl} onChange={setExcl} disabled={disabled} />
      </div>

      {/* Pinned: the numbers are the reason this screen exists, and scrolling a
          long filter form used to push them off the top of the window. It stops
          below the campaign's own pinned header rather than under it - the
          offset is measured, since that header is not a fixed height. */}
      <div className="space-y-4 xl:sticky xl:top-[var(--crm-rail-top)] xl:self-start">
        <PreviewCard
          preview={preview}
          loading={loading}
          stale={stale}
          error={error}
          deadFilters={deadFilters}
          onRefresh={() => void run(true)}
        />
        {source === "register" && <SharedAddresses threshold={campaign.sharedAddressThreshold} />}
      </div>
    </div>
  );
}
